import { z } from "zod";
//#region src/types.ts
const MCPServerStatusSchema = z.enum([
	"connected",
	"pending",
	"expired",
	"unreachable",
	"cancelled",
	"unauthorized"
]);
function slugifyMCPServerName(name) {
	return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "mcp";
}
const MCPServerSchema = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string().optional(),
	url: z.string(),
	auth_type: z.string(),
	status: MCPServerStatusSchema,
	enabled: z.boolean(),
	account_label: z.string().default(""),
	account_identity_hint: z.string().default(""),
	icon_url: z.string().default(""),
	uses_basic_auth: z.boolean().default(false)
}).transform((server) => ({
	...server,
	slug: server.slug ?? slugifyMCPServerName(server.name)
}));
const DiscoverResponseSchema = z.object({
	auth_type: z.enum(["oauth", "api_key"]),
	transport: z.enum(["streamable_http", "sse"]).optional(),
	server_name: z.string().nullable().optional(),
	oauth_fallback: z.boolean().optional().default(false)
});
const OAuthInitiateResponseSchema = z.object({
	redirect_url: z.string(),
	server_id: z.string(),
	flow_id: z.string()
});
const AddAccountResponseSchema = z.object({
	server_id: z.string(),
	redirect_url: z.string().nullable().optional(),
	flow_id: z.string().nullable().optional(),
	account: MCPServerSchema.nullable().optional()
});
const ServerStatusResponseSchema = z.object({
	status: MCPServerStatusSchema,
	status_reason: z.string().nullable().optional()
});
const MCPCatalogEntrySchema = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string(),
	url: z.string(),
	icon_url: z.string().nullable(),
	categories: z.array(z.string()).optional(),
	featured: z.boolean().optional(),
	auth_type: z.string(),
	docs_url: z.string().nullable(),
	requires_paid_plan: z.boolean().default(true)
});
const MCPCatalogResponseSchema = z.object({ entries: z.array(MCPCatalogEntrySchema) });
//#endregion
//#region src/client.ts
function createMCPClient(httpClient) {
	return {
		listServers: (probe = false) => listServers(httpClient, probe),
		discoverServer: (url) => discoverServer(httpClient, url),
		createApiKeyServer: (name, url, apiToken, optionsOrLegacyTransport = {}, legacyBasicAuthUsername = "") => {
			return createApiKeyServer(httpClient, name, url, apiToken, typeof optionsOrLegacyTransport === "string" ? legacyBasicAuthUsername : optionsOrLegacyTransport.basicAuthUsername ?? "");
		},
		initiateOAuth: (url, name, returnTo) => initiateOAuth(httpClient, url, name, returnTo),
		addAccount: (serverId, body, returnTo) => addAccount(httpClient, serverId, body, returnTo),
		updateServerLabel: (serverId, accountLabel) => updateServerLabel(httpClient, serverId, accountLabel),
		getServerStatus: (serverId) => getServerStatus(httpClient, serverId),
		reconnectServer: (serverId, returnTo) => reconnectServer(httpClient, serverId, returnTo),
		cancelPendingOAuth: (serverId, flowId) => cancelPendingOAuth(httpClient, serverId, flowId),
		deleteServer: (serverId, onlyIfPending = false) => deleteServer(httpClient, serverId, onlyIfPending),
		toggleServer: (serverId) => toggleServer(httpClient, serverId),
		setServerEnabled: (serverId, enabled) => setServerEnabled(httpClient, serverId, enabled),
		getCatalog: () => getCatalog(httpClient)
	};
}
async function listServers(httpClient, probe) {
	const response = await httpClient.get(`/mcp/servers${probe ? "?probe=true" : ""}`);
	return z.array(MCPServerSchema).parseAsync(response);
}
async function discoverServer(httpClient, url) {
	const response = await httpClient.post("/mcp/servers/discover", { url });
	return DiscoverResponseSchema.parseAsync(response);
}
async function createApiKeyServer(httpClient, name, url, apiToken, basicAuthUsername) {
	const response = await httpClient.post("/mcp/servers", {
		name,
		url,
		api_token: apiToken,
		basic_auth_username: basicAuthUsername
	});
	return MCPServerSchema.parseAsync(response);
}
async function initiateOAuth(httpClient, url, name, returnTo) {
	const response = await httpClient.post("/mcp/servers/oauth/initiate", {
		url,
		name,
		return_path: returnTo?.path,
		return_channel: returnTo?.channel
	});
	return OAuthInitiateResponseSchema.parseAsync(response);
}
async function addAccount(httpClient, serverId, body, returnTo) {
	const response = await httpClient.post(`/mcp/servers/${serverId}/accounts`, {
		...body,
		return_path: returnTo?.path,
		return_channel: returnTo?.channel
	});
	return AddAccountResponseSchema.parseAsync(response);
}
async function updateServerLabel(httpClient, serverId, accountLabel) {
	const response = await httpClient.patch(`/mcp/servers/${serverId}/label`, { account_label: accountLabel });
	return MCPServerSchema.parseAsync(response);
}
async function getServerStatus(httpClient, serverId) {
	const response = await httpClient.get(`/mcp/servers/${serverId}/status`);
	return ServerStatusResponseSchema.parseAsync(response);
}
async function reconnectServer(httpClient, serverId, returnTo) {
	const endpoint = `/mcp/servers/${serverId}/reconnect`;
	const response = await (returnTo ? httpClient.post(endpoint, {
		return_path: returnTo.path,
		return_channel: returnTo.channel
	}) : httpClient.post(endpoint));
	return OAuthInitiateResponseSchema.parseAsync(response);
}
async function cancelPendingOAuth(httpClient, serverId, flowId) {
	await httpClient.post(`/mcp/servers/${serverId}/cancel-oauth?flow_id=${encodeURIComponent(flowId)}`);
}
async function deleteServer(httpClient, serverId, onlyIfPending) {
	const params = onlyIfPending ? "?only_if_pending=true" : "";
	await httpClient.delete(`/mcp/servers/${serverId}${params}`);
}
async function toggleServer(httpClient, serverId) {
	const response = await httpClient.patch(`/mcp/servers/${serverId}/toggle`);
	return z.object({ enabled: z.boolean() }).parseAsync(response);
}
async function setServerEnabled(httpClient, serverId, enabled) {
	const response = await httpClient.patch(`/mcp/servers/${serverId}/enabled`, { enabled });
	return z.object({ enabled: z.boolean() }).parseAsync(response);
}
async function getCatalog(httpClient) {
	const response = await httpClient.get("/mcp/catalog");
	return (await MCPCatalogResponseSchema.parseAsync(response)).entries;
}
//#endregion
//#region src/utils.ts
/**
* Normalize an MCP server URL for comparison/deduplication.
* Lowercases scheme + host only (per the URI standard). Preserves path/query case,
* port, and query params. Strips trailing slashes from the path.
*/
function normalizeMCPUrl(url) {
	try {
		const u = new URL(url);
		return `${`${u.protocol}//${u.host}`.toLowerCase()}${u.pathname.replace(/\/+$/, "")}${u.search}`;
	} catch {
		return url.replace(/\/+$/, "");
	}
}
/**
* User-visible account label. "__auto_*" is the backend's internal placeholder
* for accounts connected before their real label is assigned at OAuth-callback
* time (mirrors the Composio placeholder convention) — never render it.
*/
function mcpAccountDisplayLabel(server) {
	return server.account_label.startsWith("__auto_") ? "" : server.account_label;
}
/**
* Derive the display status for an MCP server. Accounts for the `enabled`
* flag, which is orthogonal to connectivity status.
*/
function getIntegrationDisplayStatus(server) {
	if (!server.enabled) return "disabled";
	return server.status;
}
/**
* A server is "selectable" from the per-routine / per-chat picker only if it's
* globally enabled AND currently looking healthy. Servers in error states
* (expired, unauthorized, unreachable, etc.) are excluded so the user doesn't
* pick something that can't serve tools. Used by both the routine editor and
* the chat MCP picker so the contract is identical.
*
* 'probing' is treated as selectable — it's an optimistic loading overlay for
* a previously-connected server; the probe will correct it if wrong.
*/
function isSelectableMcpServer(server) {
	return server.enabled && hasConnectedMcpStatus(server);
}
function hasConnectedMcpStatus(server) {
	return server.status === "connected" || server.status === "probing";
}
function resolveSelectableEnabledMCPServers(storedIds, servers) {
	const selectableIds = new Set(servers.filter(isSelectableMcpServer).map((server) => server.id));
	const resolvedIds = [];
	const seenIds = /* @__PURE__ */ new Set();
	for (const storedId of storedIds) {
		if (!selectableIds.has(storedId) || seenIds.has(storedId)) continue;
		resolvedIds.push(storedId);
		seenIds.add(storedId);
	}
	return resolvedIds;
}
function isMCPServerNameTaken(name, servers) {
	const slug = slugifyMCPServerName(name.trim());
	return servers?.some((server) => slugifyMCPServerName(server.name) === slug && server.status === "connected") ?? false;
}
/**
* HTTP status code of a failed backend request, when the error carries one
* (desktop HttpError and mobile MCPHttpError both do). Undefined means the
* backend never responded (network error) or the error isn't HTTP at all.
*/
function getMCPErrorStatus(error) {
	if (error && typeof error === "object") {
		const { status } = error;
		if (typeof status === "number") return status;
	}
}
function extractMCPErrorMessage(error, fallback) {
	const status = getMCPErrorStatus(error);
	if (error && typeof error === "object") {
		const { body, message } = error;
		const detailMessage = extractDetailMessage(body?.detail);
		if (detailMessage?.isUserFacing) return detailMessage.message;
		if (status !== void 0 && status >= 500) return `Something went wrong on our end (error ${status}). Please try again.`;
		if (detailMessage) return detailMessage.message;
		if (status !== void 0) return `${fallback} (error ${status})`;
		if (typeof message === "string" && message && message !== "http") return message;
	}
	return fallback;
}
function extractDetailMessage(detail) {
	if (detail && typeof detail === "object" && "user_error_message" in detail) {
		const userMessage = detail.user_error_message;
		if (typeof userMessage === "string" && userMessage) return {
			message: userMessage,
			isUserFacing: true
		};
	}
	if (typeof detail === "string" && detail) return {
		message: detail,
		isUserFacing: false
	};
}
//#endregion
//#region src/workflows.ts
async function cleanupPendingMCPServerForUrl(client, url, servers) {
	const existingServers = servers ?? await client.listServers();
	const normalizedUrl = normalizeMCPUrl(url);
	const pendingServer = existingServers.find((server) => normalizeMCPUrl(server.url) === normalizedUrl && server.status === "pending");
	if (!pendingServer) return { deletedServerId: null };
	try {
		await client.deleteServer(pendingServer.id, true);
		return { deletedServerId: pendingServer.id };
	} catch (error) {
		return {
			deletedServerId: null,
			error
		};
	}
}
const TERMINAL_STATUSES = /* @__PURE__ */ new Set([
	"connected",
	"expired",
	"unreachable",
	"unauthorized"
]);
const MAX_CONSECUTIVE_ERROR_RESPONSES = 3;
async function pollMCPServerStatus(client, serverId, options = {}) {
	const maxAttempts = options.maxAttempts ?? 60;
	const intervalMs = options.intervalMs ?? 5e3;
	let lastError;
	let consecutiveErrorResponses = 0;
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		if (!await waitForPollInterval(intervalMs, options.signal)) return {
			type: "aborted",
			attempts: attempt - 1
		};
		try {
			const response = await client.getServerStatus(serverId);
			if (options.signal?.aborted) return {
				type: "aborted",
				attempts: attempt
			};
			consecutiveErrorResponses = 0;
			const terminal = resolveTerminalResult(response, attempt);
			if (terminal) return terminal;
		} catch (error) {
			if (options.signal?.aborted) return {
				type: "aborted",
				attempts: attempt
			};
			lastError = error;
			const status = getMCPErrorStatus(error);
			if (status === void 0) {
				consecutiveErrorResponses = 0;
				continue;
			}
			consecutiveErrorResponses++;
			if (status === 404 || consecutiveErrorResponses >= MAX_CONSECUTIVE_ERROR_RESPONSES) return {
				type: "failed",
				error,
				status,
				attempts: attempt
			};
		}
	}
	return {
		type: "timeout",
		attempts: maxAttempts,
		lastError
	};
}
function resolveTerminalResult(response, attempt) {
	if (!TERMINAL_STATUSES.has(response.status)) return null;
	if (response.status === "connected") return {
		type: "connected",
		response,
		attempts: attempt
	};
	if (response.status === "expired") return {
		type: response.status_reason === "cancelled" ? "cancelled" : "expired",
		response,
		attempts: attempt
	};
	return {
		type: "expired",
		response,
		attempts: attempt
	};
}
async function waitForPollInterval(intervalMs, signal) {
	if (signal?.aborted) return false;
	return new Promise((resolve) => {
		const timeout = setTimeout(() => {
			signal?.removeEventListener("abort", abort);
			resolve(true);
		}, intervalMs);
		const abort = () => {
			clearTimeout(timeout);
			resolve(false);
		};
		signal?.addEventListener("abort", abort, { once: true });
	});
}
//#endregion
export { AddAccountResponseSchema, DiscoverResponseSchema, MCPCatalogEntrySchema, MCPCatalogResponseSchema, MCPServerSchema, OAuthInitiateResponseSchema, ServerStatusResponseSchema, cleanupPendingMCPServerForUrl, createMCPClient, extractMCPErrorMessage, getIntegrationDisplayStatus, hasConnectedMcpStatus, isMCPServerNameTaken, isSelectableMcpServer, mcpAccountDisplayLabel, normalizeMCPUrl, pollMCPServerStatus, resolveSelectableEnabledMCPServers, slugifyMCPServerName };

//# sourceMappingURL=index.js.map